/**
 * Tiered Pricing - Intercepta cantidad y selecciona variante correcta
 * Trabaja sobre los componentes nativos sin modificarlos
 */

class TieredPricing {
  constructor() {
    this.table = document.querySelector('[data-tiered-pricing-table]');
    if (!this.table) return;

    this.init();
  }

  init() {
    // Obtener datos de los tiers
    const dataScript = this.table.querySelector('[data-tiered-pricing-data]');
    if (!dataScript) return;

    try {
      this.tiersData = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.error('Error parsing tiered pricing data:', e);
      return;
    }

    // Verificar que hay tiers
    if (!this.tiersData.tiers || this.tiersData.tiers.length === 0) {
      console.warn('Tiered Pricing: No hay tiers configurados');
      return;
    }

    // Guardar traducciones
    this.i18n = this.tiersData.i18n || {
      maxQuantity: 'Maximum quantity',
      minQuantity: 'Minimum quantity',
      notAvailable: 'This quantity is not available',
      youSave: 'You save'
    };

    // Obtener elementos nativos
    this.quantityInput = document.querySelector('[data-js-product-quantity] input[type="number"], [data-js-product-quantity] .qty-selector');
    this.productPage = document.querySelector('[data-js-product-component]');
    
    if (!this.quantityInput) {
      console.warn('Tiered Pricing: No se encontró el input de cantidad');
      return;
    }

    // Marcar el producto como "tiene tiered pricing" (CSS lo ocultará automáticamente)
    if (this.productPage) {
      this.productPage.setAttribute('data-has-tiered-pricing', 'true');
    }

    // Calcular límites de cantidad
    this.calculateQuantityLimits();

    // Aplicar validaciones al input
    this.applyQuantityValidations();

    // Escuchar cambios en cantidad
    this.quantityInput.addEventListener('input', this.handleQuantityChange.bind(this));
    this.quantityInput.addEventListener('change', this.handleQuantityChange.bind(this));

    // Interceptar submit del formulario para validar antes de agregar al carrito
    const productForm = this.quantityInput.closest('form');
    if (productForm) {
      productForm.addEventListener('submit', (e) => {
        if (!this.validateAndAdjustQuantity()) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }, true); // useCapture = true para interceptar antes que otros handlers
    }

    // Inicializar: establecer valor mínimo si el input está vacío o tiene valor inválido
    const currentValue = parseInt(this.quantityInput.value);
    if (isNaN(currentValue) || currentValue < this.minQuantity) {
      this.quantityInput.value = this.minQuantity;
    }
    
    // Actualizar tier inicial sin mostrar mensajes
    this.handleQuantityChange();
  }

  calculateQuantityLimits() {
    // Encontrar el máximo de todos los tiers
    this.maxQuantity = Math.max(...this.tiersData.tiers.map(tier => tier.maxQty || tier.minQty));
    this.minQuantity = Math.min(...this.tiersData.tiers.map(tier => tier.minQty));
    
    // Detectar si TODOS los tiers son cantidades exactas
    this.allTiersAreExact = this.tiersData.tiers.every(tier => 
      tier.isExact === true || tier.isExact === "true"
    );
    
    // Si todos son exactos, crear lista de cantidades válidas
    if (this.allTiersAreExact) {
      this.exactQuantities = this.tiersData.tiers
        .map(tier => parseInt(tier.minQty))
        .sort((a, b) => a - b);
    }
  }

  applyQuantityValidations() {
    // Establecer atributos min/max en el input
    this.quantityInput.setAttribute('min', this.minQuantity);
    this.quantityInput.setAttribute('max', this.maxQuantity);

    // Validación en tiempo real SOLO para el MÁXIMO (evita confusión)
    this.quantityInput.addEventListener('input', (e) => {
      const value = parseInt(e.target.value);
      
      // Solo validar si el valor excede el máximo
      if (!isNaN(value) && value > this.maxQuantity) {
        e.target.value = this.maxQuantity;
        this.showValidationMessage(`${this.i18n.maxQuantity}: ${this.formatNumber(this.maxQuantity)}`);
        // Disparar handleQuantityChange para actualizar el precio
        this.handleQuantityChange();
      }
    });

    // Validación al perder foco (blur) - valida mínimo, máximo y cantidades exactas
    this.quantityInput.addEventListener('blur', (e) => {
      const value = parseInt(e.target.value);
      
      if (isNaN(value) || value < this.minQuantity) {
        e.target.value = this.minQuantity;
        this.showValidationMessage(`${this.i18n.minQuantity}: ${this.formatNumber(this.minQuantity)}`);
        this.handleQuantityChange();
      } else if (value > this.maxQuantity) {
        e.target.value = this.maxQuantity;
        this.showValidationMessage(`${this.i18n.maxQuantity}: ${this.formatNumber(this.maxQuantity)}`);
        this.handleQuantityChange();
      } else if (this.allTiersAreExact) {
        // Si todos los tiers son exactos, ajustar a la cantidad exacta más cercana
        const nearestExact = this.findNearestExactQuantity(value);
        if (nearestExact !== value) {
          e.target.value = nearestExact;
          // Mostrar las cantidades válidas disponibles
          const validQuantities = this.exactQuantities.map(q => this.formatNumber(q)).join(', ');
          this.showValidationMessage(`Cantidades disponibles: ${validQuantities}`);
          this.handleQuantityChange();
        }
      }
    });
  }

  showValidationMessage(message) {
    // Buscar o crear contenedor de mensaje
    let messageEl = document.querySelector('.tiered-pricing-validation-message');
    
    if (!messageEl) {
      messageEl = document.createElement('div');
      messageEl.className = 'tiered-pricing-validation-message';
      this.quantityInput.parentElement.appendChild(messageEl);
    }

    messageEl.textContent = message;
    messageEl.classList.add('show');

    // Ocultar después de 3 segundos
    clearTimeout(this.validationTimeout);
    this.validationTimeout = setTimeout(() => {
      messageEl.classList.remove('show');
    }, 3000);
  }

  findNearestExactQuantity(quantity) {
    // Si no todos los tiers son exactos, retornar la cantidad original
    if (!this.allTiersAreExact) {
      return quantity;
    }
    
    // Buscar la cantidad exacta más cercana
    let nearest = this.exactQuantities[0]; // Por defecto, la primera (mínima)
    let minDiff = Math.abs(quantity - nearest);
    
    for (const exactQty of this.exactQuantities) {
      const diff = Math.abs(quantity - exactQty);
      if (diff < minDiff) {
        minDiff = diff;
        nearest = exactQty;
      }
    }
    
    return nearest;
  }

  validateAndAdjustQuantity() {
    const value = parseInt(this.quantityInput.value);
    
    // Validar que sea un número
    if (isNaN(value)) {
      this.quantityInput.value = this.minQuantity;
      this.showValidationMessage(`${this.i18n.minQuantity}: ${this.formatNumber(this.minQuantity)}`);
      this.handleQuantityChange();
      return false;
    }
    
    // Validar mínimo
    if (value < this.minQuantity) {
      this.quantityInput.value = this.minQuantity;
      this.showValidationMessage(`${this.i18n.minQuantity}: ${this.formatNumber(this.minQuantity)}`);
      this.handleQuantityChange();
      return false;
    }
    
    // Validar máximo
    if (value > this.maxQuantity) {
      this.quantityInput.value = this.maxQuantity;
      this.showValidationMessage(`${this.i18n.maxQuantity}: ${this.formatNumber(this.maxQuantity)}`);
      this.handleQuantityChange();
      return false;
    }
    
    // Si todos los tiers son exactos, validar que la cantidad sea exacta
    if (this.allTiersAreExact) {
      const nearestExact = this.findNearestExactQuantity(value);
      if (nearestExact !== value) {
        this.quantityInput.value = nearestExact;
        const validQuantities = this.exactQuantities.map(q => this.formatNumber(q)).join(', ');
        this.showValidationMessage(`Cantidades disponibles: ${validQuantities}`);
        this.handleQuantityChange();
        return false; // Prevenir submit, usuario debe confirmar la cantidad ajustada
      }
    }
    
    // Verificar que existe un tier válido
    const tier = this.findTierForQuantity(value);
    if (!tier) {
      // No hay tier válido, ajustar al más cercano
      if (this.allTiersAreExact) {
        const nearestExact = this.findNearestExactQuantity(value);
        this.quantityInput.value = nearestExact;
        const validQuantities = this.exactQuantities.map(q => this.formatNumber(q)).join(', ');
        this.showValidationMessage(`Cantidades disponibles: ${validQuantities}`);
      } else {
        this.showValidationMessage(this.i18n.notAvailable);
      }
      this.handleQuantityChange();
      return false;
    }
    
    // Todo OK, permitir submit
    return true;
  }

  handleQuantityChange() {
    const quantity = parseInt(this.quantityInput.value);
    
    // Si el input está vacío o no es un número válido, no hacer nada
    // Esto permite al usuario borrar y escribir libremente
    if (isNaN(quantity)) {
      return;
    }
    
    // NO validar límites aquí - solo buscar el tier correspondiente
    // La validación de límites se hace en blur
    
    // Encontrar el tier correcto
    const tier = this.findTierForQuantity(quantity);
    
    if (!tier) {
      // Si no hay tier, no hacer nada (permite escribir valores intermedios)
      // La validación completa se hace en validateAndAdjustQuantity() antes del submit
      return;
    }

    // 1. Actualizar input hidden directamente (garantiza que el submit funcione)
    this.updateVariantInput(tier.variantId);

    // 2. Actualizar precio y stock manualmente (sin usar el picker)
    this.updatePriceAndStock(tier);

    // 3. Actualizar visual de la tabla
    this.updateTableHighlight(tier.variantId);
  }

  findTierForQuantity(quantity) {
    // Buscar el tier exacto o el rango que corresponda
    const tier = this.tiersData.tiers.find(tier => {
      // Convertir isExact a booleano si viene como string
      const isExact = tier.isExact === true || tier.isExact === "true";
      
      if (isExact) {
        // Tier exacto: la cantidad debe ser exactamente minQty
        return quantity === parseInt(tier.minQty);
      } else {
        // Tier rango: la cantidad debe estar entre minQty y maxQty
        const min = parseInt(tier.minQty);
        const max = parseInt(tier.maxQty);
        return quantity >= min && quantity <= max;
      }
    });

    if (!tier) {
      // Si no se encuentra tier, buscar el último tier (para cantidades superiores)
      const lastTier = this.tiersData.tiers[this.tiersData.tiers.length - 1];
      if (lastTier && quantity > parseInt(lastTier.maxQty)) {
        return lastTier;
      }
    }

    return tier;
  }

  updateVariantInput(variantId) {
    // Actualizar TODOS los inputs con name="id" (puede haber múltiples)
    const variantInputs = document.querySelectorAll('input[name="id"]');
    
    if (variantInputs.length === 0) {
      console.warn('Tiered Pricing: No se encontró input[name="id"]');
      return;
    }

    variantInputs.forEach(input => {
      input.value = variantId;
    });
  }

  updatePriceAndStock(tier) {
    const quantity = parseInt(this.quantityInput.value) || 1;
    
    // Calcular precio total (precio unitario × cantidad)
    const unitPrice = tier.price;
    const totalPrice = unitPrice * quantity;

    // Buscar el elemento de precio nativo
    const priceContainer = document.querySelector('[data-update-block="price-extended"]');
    if (!priceContainer) {
      console.warn('Tiered Pricing: No se encontró [data-update-block="price-extended"]');
      return;
    }

    const priceElement = priceContainer.querySelector('.product-price--original');
    if (!priceElement) {
      console.warn('Tiered Pricing: No se encontró .product-price--original');
      return;
    }

    // Crear HTML del precio con formato nativo
    const formattedUnitPrice = this.formatMoney(unitPrice);
    const formattedTotalPrice = this.formatMoney(totalPrice);

    // Calcular ahorro vs tier más caro
    const savingsHTML = this.calculateSavings(quantity, unitPrice);

    // 1. FEEDBACK VISUAL: Añadir animación de actualización
    priceElement.style.transition = 'opacity 0.2s ease';
    priceElement.style.opacity = '0.5';

    setTimeout(() => {
      // Formatear la cantidad con separadores de miles
      const formattedQuantity = this.formatNumber(quantity);
      
      // Limpiar y actualizar el precio completo
      // Layout: Precio + Info (desglose arriba, ahorro abajo)
      priceElement.innerHTML = `
        <span class="visually-hidden">Precio total</span>
        <span class="tiered-price-total">${formattedTotalPrice}</span>
        <span class="tiered-price-info-wrapper">
          <span class="tiered-price-breakdown">(${formattedUnitPrice} × ${formattedQuantity})</span>
          ${savingsHTML}
        </span>
      `;
      
      // Restaurar opacidad con animación
      priceElement.style.opacity = '1';
      
      // Añadir clase temporal para animación adicional
      priceElement.classList.add('price-updated');
      setTimeout(() => priceElement.classList.remove('price-updated'), 600);
    }, 150);
  }

  calculateSavings(quantity, currentUnitPrice) {
    // Encontrar el tier más caro (primer tier)
    const mostExpensiveTier = this.tiersData.tiers[0];
    if (!mostExpensiveTier || currentUnitPrice >= mostExpensiveTier.price) {
      return ''; // No hay ahorro
    }

    // Calcular ahorro total
    const savingsPerUnit = mostExpensiveTier.price - currentUnitPrice;
    const totalSavings = savingsPerUnit * quantity;

    if (totalSavings <= 0) return '';

    const formattedSavings = this.formatMoney(totalSavings);
    
    // 2. INDICADOR DE AHORRO
    return `
      <span class="tiered-price-savings">
       <span>${this.i18n.youSave} ${formattedSavings}</span>
      </span>
    `;
  }

  formatMoney(cents) {
    // Usar el formato de la tienda si está disponible
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    
    // Formato español: separador de miles (punto) y decimales (coma)
    const amount = cents / 100;
    const formatted = new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    
    return formatted;
  }

  formatNumber(number) {
    // Formatear número entero con separadores de miles (formato español)
    return new Intl.NumberFormat('es-ES').format(number);
  }

  updateTableHighlight(variantId) {
    const rows = this.table.querySelectorAll('[data-tier-row]');
    rows.forEach(row => {
      if (row.dataset.variantId == variantId) {
        row.classList.add('is-active');
      } else {
        row.classList.remove('is-active');
      }
    });
  }
}

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new TieredPricing());
} else {
  new TieredPricing();
}

// Reinicializar en cambios de sección (Shopify theme editor)
document.addEventListener('shopify:section:load', () => new TieredPricing());
